import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { CrowdfundingProgressbarResponse5 } from 'src/app/models/crowdfunding/progressbar.model';
import { TypesenseService } from '../../services/typesense.service';
import { SubscriptionService } from '../../services/subscription.service';
import { Role, SubscriptionInfoModel } from 'src/app/models/subscription-info.model';
import { AuthService } from '../../services/auth.service';
import { UserApiService } from '../../services/user-api.service';
import { CountdownTimerService, CountdownTime } from '../../services/countdown-timer.service';
import { formatUtcDateToETDisplay, parseApiDateToUtc } from 'src/app/utils/date-utils';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
    selector: 'app-invest-invitation-popup',
    templateUrl: './invest-invitation-popup.component.html',
    styleUrls: ['./invest-invitation-popup.component.scss'],
    standalone: false
})
export class InvestInvitationPopupComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject<void>();

   @ViewChild('investInvitationPopup', { static: false })
   investInvitationPopup!: TemplateRef<any>;

   private popupTimer: ReturnType<typeof setTimeout> | null = null;
   private checkModalInterval: ReturnType<typeof setInterval> | null = null;
   private postModalTimeout: ReturnType<typeof setTimeout> | null = null;
   private isBrowser: boolean;
   private modalRef?: NgbModalRef;

   data!: CrowdfundingProgressbarResponse5;

   userNewRole: Role | null = null;
   isLoggedIn: boolean = false;
   userFirstName: string | null = null;

   private readonly excludedRoutes = [
      '/halal-trading-waitlist',
      '/supported-countries',
      '/supported-brokerages',
      '/investing-coverage',
      '/pricing',
      '/authentication'
   ];

   // private readonly SHOWN_KEY = 'investInvitationPopupShown';
   private readonly INVEST_URL_GENERAL = 'https://invest.musaffa.com?utm_source=mainwebsite&utm_medium=general_popup&utm_campaign=6_years_free_trading';
   private readonly INVEST_URL_NABIL_FEKIR = 'https://invest.musaffa.com?utm_source=mainwebsite&utm_medium=nabil_fekir_popup&utm_campaign=6_years_free_trading';
   private readonly SUPPRESS_KEY = 'investmentPopupSuppressUntil';
   private readonly DISMISS_COUNT_KEY = 'investmentPopupDismissCount';
   private readonly SUPPRESS_DAYS = [3, 7, 14];
   private readonly POPUP_DELAY_MS = 30000;
   private readonly DAILY_POPUP_END_DATE = new Date('2026-02-23T23:59:59');
   // private readonly DAILY_SHOWN_AT_KEY = 'investmentPopupLastShownAt';
   private readonly PRE_CUTOFF_SESSION_KEY = 'investInvitationPopupShownThisSession';

   // Add near other properties
   userCountryCode: string | null = null;

   // Europe ISO country codes (you can extend if needed)
   private readonly EUROPE_COUNTRY_CODES = new Set([
      'AL', 'AD', 'AT', 'BE', 'BA', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR',
      'GB', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK',
      'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'SE', 'SI', 'SK', 'SM', 'UA', 'VA'
   ]);

   isLgUp = false;
   private readonly FALLBACK_OFFER_END_DATE_STR = '2026-02-23 04:59:59';
   private readonly FALLBACK_OFFER_END_DATE = parseApiDateToUtc(this.FALLBACK_OFFER_END_DATE_STR);
   isCountdownLoading = true;
   countdown$: Observable<CountdownTime> = this.countdownTimerService.createCountdown(this.FALLBACK_OFFER_END_DATE);
   offerEndDisplay = '';
   isEventBannerEnabled = false;
   private isResolvingPopup = false;

   constructor(
      private typesenseService: TypesenseService,
      private modalService: NgbModal,
      private router: Router,
      private subscriptionService: SubscriptionService,
      private auth: AuthService,
      private userApiService: UserApiService,
      private breakpointObserver: BreakpointObserver,
      private countdownTimerService: CountdownTimerService,
      @Inject(PLATFORM_ID) platformId: object,
   ) {
      this.isBrowser = isPlatformBrowser(platformId);
   }

   async ngOnInit(): Promise<void> {
      if (!this.isBrowser) {
         return;
      }

      this.loadEventBannerConfig();

      this.breakpointObserver
         .observe(['(min-width: 992px)'])
         .pipe(takeUntil(this.destroy$))
         .subscribe(result => {
            this.isLgUp = result.matches;
         });

      SubscriptionService.subscriptionData
         .pipe(takeUntil(this.destroy$))
         .subscribe((data: SubscriptionInfoModel) => {
            this.userNewRole = data.newRole;
            this.isLoggedIn = !!this.auth.getUserId();
            if (this.isLoggedIn) {
               this.userFirstName = localStorage.getItem('firstname') ?? '';
            }
         })

      this.router.events
         .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
         )
         .subscribe(() => this.resetPopupTimer());

      this.resetPopupTimer();
   }

   private async resetPopupTimer(): Promise<void> {
      if (this.isResolvingPopup) return;
      this.isResolvingPopup = true;

      try {
         // 🚫 DO NOT SHOW popup if user opted out
         if (this.auth.getMusaffaInvestmentOptoutValue()) {
            return;
         }

         if (this.isPaidUser) return;

         if (!this.isEventBannerEnabled) return;

         this.clearTimer();

         if (this.isExcludedRoute()) return;

         // 🟡 Before Feb 19 → once per browser session
         if (this.isDailyPopupPeriod()) {
            if (sessionStorage.getItem(this.PRE_CUTOFF_SESSION_KEY)) return;
         }
         // 🔁 After Feb 19 → respect suppression window
         else {
            if (!this.isEligibleByDate()) return;
         }

         this.popupTimer = setTimeout(async () => {
            await this.resolveUserCountry();
            if (!this.userCountryCode) return;

            this.tryOpenPopup();
         }, this.POPUP_DELAY_MS);

      } finally {
         this.isResolvingPopup = false;
      }
   }

   private openPopup(): void {
      if (this.auth.getMusaffaInvestmentOptoutValue()) return;
      if (this.isPaidUser) return;
      if (!this.isEventBannerEnabled) return;
      if (this.modalRef) return;
      if (this.isExcludedRoute()) return;

      // ✅ only reset countdown data
      this.resetCountdown();

      this.fetchData();

      this.modalRef = this.modalService.open(this.investInvitationPopup, {
         centered: true,
         backdrop: 'static',
         size: 'xl',
         windowClass: 'invest-invitation-modal'
      });

      this.modalRef.closed.subscribe(() => {
         this.resetCountdown();
         this.modalRef = undefined;
      });
   }

   private resetCountdown(): void {
      this.isCountdownLoading = true;
   }

   onInvest(modal: NgbModalRef, popupType: 'general' | 'nabil_fekir'): void {
      if (!this.isDailyPopupPeriod()) {
         this.suppressProgressively();
         // sessionStorage.removeItem(this.SHOWN_KEY);
      }

      modal.close();

      const url =
         popupType === 'general'
            ? this.INVEST_URL_GENERAL
            : this.INVEST_URL_NABIL_FEKIR;

      window.open(url, '_blank', 'noopener,noreferrer');
   }

   onMaybeLater(modal: NgbModalRef): void {
      if (!this.isDailyPopupPeriod()) {
         this.suppressProgressively();
         // sessionStorage.removeItem(this.SHOWN_KEY);
      }

      modal.close();
   }

   private fetchData() {
      this.typesenseService
         .fetchCrowdfundingProgressData5()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: data => {
               this.data = data;
               const raw = data.event_end_date;

               const endDate = raw ? parseApiDateToUtc(raw) : this.FALLBACK_OFFER_END_DATE;
               this.countdown$ = this.countdownTimerService.createCountdown(endDate);
               this.offerEndDisplay = formatUtcDateToETDisplay(endDate);
               this.isCountdownLoading = false;
            },
            error: err => {
               console.error('Failed to fetch crowdfunding data', err);
               // API failed → fallback date
               this.countdown$ = this.countdownTimerService.createCountdown(this.FALLBACK_OFFER_END_DATE);
               this.isCountdownLoading = false;
               this.offerEndDisplay = formatUtcDateToETDisplay(this.FALLBACK_OFFER_END_DATE);
            }
         });
   }

   private suppressProgressively(): void {
      const dismissCount =
         Number(localStorage.getItem(this.DISMISS_COUNT_KEY)) || 0;

      const nextCount = dismissCount + 1;

      const days =
         this.SUPPRESS_DAYS[Math.min(nextCount - 1, this.SUPPRESS_DAYS.length - 1)];

      const suppressUntil =
         Date.now() + days * 24 * 60 * 60 * 1000;

      localStorage.setItem(this.SUPPRESS_KEY, suppressUntil.toString());
      localStorage.setItem(this.DISMISS_COUNT_KEY, nextCount.toString());
   }

   private isEligibleByDate(): boolean {
      const suppressUntil = localStorage.getItem(this.SUPPRESS_KEY);
      return !suppressUntil || Date.now() > Number(suppressUntil);
   }

   private isExcludedRoute(): boolean {
      if (!this.isBrowser) return true;

      const url = this.router.url.split('?')[0]; // ignore query params

      return this.excludedRoutes.some(route =>
         url === route || url.startsWith(route + '/')
      );
   }

   private clearTimer(): void {
      if (this.popupTimer) {
         clearTimeout(this.popupTimer);
         this.popupTimer = null;
      }
      this.clearWaitTimers();
   }

   private tryOpenPopup(): void {
      if (this.modalService.hasOpenModals()) {
         this.waitForModalsToClose();
      } else {
         this.openPopup();
         
         // ✅ mark shown for this browser session (pre-Feb 19)
         if (this.isDailyPopupPeriod()) {
            sessionStorage.setItem(this.PRE_CUTOFF_SESSION_KEY, 'true');
         }
      }
   }

   private waitForModalsToClose(): void {
      this.clearWaitTimers();

      this.checkModalInterval = setInterval(() => {
         if (!this.modalService.hasOpenModals()) {
            if (this.checkModalInterval) {
               clearInterval(this.checkModalInterval);
               this.checkModalInterval = null;
            }

            this.postModalTimeout = setTimeout(() => {
               this.tryOpenPopup();
            }, 2000);
         }
      }, 1000);
   }

   private clearWaitTimers(): void {
      if (this.checkModalInterval) {
         clearInterval(this.checkModalInterval);
         this.checkModalInterval = null;
      }
      if (this.postModalTimeout) {
         clearTimeout(this.postModalTimeout);
         this.postModalTimeout = null;
      }
   }

   get isFreeUser(): boolean {
      return this.isLoggedIn && this.userNewRole === 'free';
   }

   get isPaidUser(): boolean {
      return (
         this.isLoggedIn &&
         this.userNewRole !== 'free' &&
         this.userNewRole !== null
      );
   }

   private async resolveUserCountry(): Promise<void> {
      let storedCode = localStorage.getItem('countryCode');

      // If not present yet, wait briefly for AppComponent to set it
      if (!storedCode) {
         await new Promise(resolve => setTimeout(resolve, 300));
         storedCode = localStorage.getItem('countryCode');
      }

      this.userCountryCode = storedCode ? storedCode.toUpperCase() : null;
      // console.log('Resolved country:', this.userCountryCode);
   }

   get isEuropeanUser(): boolean {
      if (!this.userCountryCode) return false;
      return this.EUROPE_COUNTRY_CODES.has(this.userCountryCode);
   }

   private isDailyPopupPeriod(): boolean {
      return Date.now() <= this.DAILY_POPUP_END_DATE.getTime();
   }

   // private canShowDailyPopup(): boolean {
   //    const lastShown = Number(localStorage.getItem(this.DAILY_SHOWN_AT_KEY));
   //    if (!lastShown) return true;

   //    const HOURS_24 = 24 * 60 * 60 * 1000;
   //    return Date.now() - lastShown >= HOURS_24;
   // }

   private loadEventBannerConfig(): void {
      this.typesenseService
         .getConfigData()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: config => {
               const showEventPopup = config?.prod?.show_event_popup;

               // key must EXIST and be true
               this.isEventBannerEnabled =
                  typeof showEventPopup === 'boolean'
                     ? showEventPopup
                     : false;

               if (this.isEventBannerEnabled) {
                  this.resetPopupTimer();
               } else {
                  this.clearTimer();
               }
            },
            error: () => {
               // fail-safe: disable popup
               this.isEventBannerEnabled = false;
               this.clearTimer();
            }
         });
   }

   ngOnDestroy(): void {
      this.clearTimer();
      this.destroy$.next();
      this.destroy$.complete();
   }

}
